import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core';
import { DrawerSetting, GridColumn } from '@grid/abstractions';
import { TranslateService } from '@ngx-translate/core';
import { ActionButton } from '@shared/abstraction/grid/action-button';
import { SearchSetting } from '@shared/abstraction/grid/search-setting';
import { DataType } from '@shared/helpers/enums/data-type';
import { CurrentUserService } from '@shared/services/current-user.service';
import { QueryOptions } from 'odata-query';
import { isGstarSystemUser } from '../../helpers/is-gstar-system-user';
import {
  isSignedInUsersRow,
  signedInSnapshot,
} from '../../helpers/is-signed-in-users-row';
import {
  Collection,
  GridItemCreateEvent,
  GridItemUpdateEvent,
  InternalUser,
  InternalUserWithRoles,
} from '../../models';
import { UpsertInternalUserComponent } from '../upsert-internal-user/upsert-internal-user.component';
import { UserEngagementsCellComponent } from '../user-engagements-cell/user-engagements-cell.component';

@Component({
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'gstar-internal-users-grid',
  templateUrl: 'internal-users-grid.component.html',
})
export class InternalUsersGridComponent {
  private translateService = inject(TranslateService);
  private readonly currentUserService = inject(CurrentUserService);

  @Input({ required: true }) source: Collection<InternalUser> = {
    total: 0,
    items: [],
  };

  @Input() query!: Partial<QueryOptions<InternalUser>>;
  @Output() queryChange = new EventEmitter<
    Partial<QueryOptions<InternalUser>>
  >();

  @Output() create = new EventEmitter<
    GridItemCreateEvent<InternalUserWithRoles>
  >();
  @Output() update = new EventEmitter<
    GridItemUpdateEvent<InternalUserWithRoles>
  >();

  @Output() deleteRequested = new EventEmitter<InternalUser>();

  actionButtons: ActionButton<InternalUser>[] = [
    {
      label: this.translateService.instant('admin.user.deleteUser'),
      icon: 'icon-delete-outline',
      disabledIf: (item: InternalUser): boolean =>
        isGstarSystemUser(item) ||
        isSignedInUsersRow(
          { id: item.id, email: item.email },
          signedInSnapshot(this.currentUserService.currentUserInfo())
        ),
      command: (item: InternalUser): void => {
        this.deleteRequested.emit(item);
      },
    },
  ];

  drawerSetting: DrawerSetting<InternalUser, InternalUserWithRoles> = {
    create: {
      title: this.translateService.instant(
        'admin.grid.createInternalUserTitle'
      ),
      component: UpsertInternalUserComponent,
      buttons: [
        {
          label: this.translateService.instant('admin.grid.cancel'),
          command: (closeFn): void => {
            closeFn();
          },
          isPrimary: false,
        },
        {
          label: this.translateService.instant('admin.grid.create'),
          command: (closeFn, item): void => {
            if (item?.isValid) {
              this.create.emit({ item: item.model!, onSuccess: closeFn });
            }
          },
          isPrimary: true,
        },
      ],
    },
    edit: {
      title: this.translateService.instant(
        'admin.grid.updateInternalUserTitle'
      ),
      component: UpsertInternalUserComponent,
      buttons: [
        {
          label: this.translateService.instant('admin.grid.cancel'),
          command: (closeFn): void => {
            closeFn();
          },
          isPrimary: false,
        },
        {
          label: this.translateService.instant('admin.grid.update'),
          command: (closeFn, item): void => {
            if (item?.isValid) {
              this.update.emit({
                item: item.model!,
                prevItem: item.prevModel,
                onSuccess: closeFn,
              });
            }
          },
          isPrimary: true,
        },
      ],
    },
  };

  headers: GridColumn[] = [
    {
      label: this.translateService.instant('admin.user.userColumn'),
      key: 'fullName',
      dataType: DataType.String,
      isSortable: true,
    },
    {
      label: this.translateService.instant('internalUser.email'),
      key: 'email',
      dataType: DataType.String,
      isSortable: true,
    },
    {
      label: this.translateService.instant('admin.user.engagementsColumn'),
      key: 'engagementAssignments',
      dataType: DataType.Template,
      templateCell: UserEngagementsCellComponent,
    },
    {
      label: this.translateService.instant('internalUser.modified'),
      key: 'modified',
      dataType: DataType.Date,
      isSortable: true,
    },
    {
      label: this.translateService.instant('internalUser.isActive'),
      key: 'isActive',
      dataType: DataType.Boolean,
      isSortable: true,
    },
  ];

  searchSetting: SearchSetting<InternalUser> = {
    filtersLogicalOperator: 'or',
    filters: [
      {
        dataKey: 'firstName',
        operator: 'startswith',
      },
      {
        dataKey: 'lastName',
        operator: 'startswith',
      },
      {
        dataKey: 'email',
        operator: 'contains',
      },
    ],
  };

  queryHandler(internalUserQuery: Partial<QueryOptions<InternalUser>>): void {
    this.queryChange.emit(internalUserQuery);
  }
}
